/* eslint-disable react-refresh/only-export-components -- context provider exports hook utilities */
import {
	createContext,
	useContext,
	useEffect,
	useState,
	ReactNode,
} from "react";
import { User, Session, AuthError } from "@supabase/supabase-js";
import { supabase } from "@shared/integrations/supabase/client";

interface AuthContextType {
	user: User | null;
	session: Session | null;
	loading: boolean;
	signUp: (
		email: string,
		password: string,
	) => Promise<{ error: AuthError | null }>;
	signIn: (
		email: string,
		password: string,
	) => Promise<{ error: AuthError | null }>;
	signOut: () => Promise<void>;
	resetPassword: (email: string) => Promise<{ error: AuthError | null }>;
	updatePassword: (newPassword: string) => Promise<{ error: AuthError | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const isStaleSessionError = (error: AuthError) => {
	const message = error.message.toLowerCase();

	// Don't sign the user out on transient connectivity issues.
	if (message.includes("fetch") || message.includes("network")) {
		return false;
	}

	const status = (error as AuthError & { status?: number }).status;
	return (
		status === 401 ||
		status === 403 ||
		status === 404 ||
		message.includes("user not found") ||
		message.includes("invalid jwt")
	);
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
	const [user, setUser] = useState<User | null>(null);
	const [session, setSession] = useState<Session | null>(null);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		let isMounted = true;

		// Set up auth state listener
		const {
			data: { subscription },
		} = supabase.auth.onAuthStateChange((event, session) => {
			if (!isMounted || event === "INITIAL_SESSION") return;

			setSession(session);
			setUser(session?.user ?? null);
			setLoading(false);
		});

		const validateExistingSession = async () => {
			const {
				data: { session },
			} = await supabase.auth.getSession();

			if (!isMounted) return;

			if (!session) {
				setSession(null);
				setUser(null);
				setLoading(false);
				return;
			}

			const { data, error } = await supabase.auth.getUser();

			if (!isMounted) return;

			if (error && isStaleSessionError(error)) {
				await supabase.auth.signOut({ scope: "local" });

				if (!isMounted) return;

				setSession(null);
				setUser(null);
				setLoading(false);

				if (window.location.pathname !== "/auth") {
					window.location.replace("/auth");
				}
				return;
			}

			setSession(session);
			setUser(data.user ?? session.user);
			setLoading(false);
		};

		void validateExistingSession();

		return () => {
			isMounted = false;
			subscription.unsubscribe();
		};
	}, []);

	const signUp = async (email: string, password: string) => {
		const redirectUrl = `${window.location.origin}/`;

		const { error } = await supabase.auth.signUp({
			email,
			password,
			options: {
				emailRedirectTo: redirectUrl,
			},
		});
		return { error };
	};

	const signIn = async (email: string, password: string) => {
		const { error } = await supabase.auth.signInWithPassword({
			email,
			password,
		});
		return { error };
	};

	const signOut = async () => {
		await supabase.auth.signOut();
	};

	const resetPassword = async (email: string) => {
		const redirectUrl = `${window.location.origin}/auth`;

		const { error } = await supabase.auth.resetPasswordForEmail(email, {
			redirectTo: redirectUrl,
		});
		return { error };
	};

	const updatePassword = async (newPassword: string) => {
		const { error } = await supabase.auth.updateUser({
			password: newPassword,
		});
		return { error };
	};

	return (
		<AuthContext.Provider
			value={{
				user,
				session,
				loading,
				signUp,
				signIn,
				signOut,
				resetPassword,
				updatePassword,
			}}
		>
			{children}
		</AuthContext.Provider>
	);
};

export const useAuth = () => {
	const context = useContext(AuthContext);
	if (context === undefined) {
		throw new Error("useAuth must be used within an AuthProvider");
	}
	return context;
};
